import { uniq } from 'lodash';
import {
  FuelEfficiencyUnit,
  PriceRuleSource,
} from '@upstart/harmony/lib/constants';

const feedTypes = ['csv', 'shiftApi', 'shiftFtp', 'autodata'];

const priceFallbacks = {
  type: 'object',
  title: 'Price Fallbacks',
  description:
    'Fallbacks are not propagated. For example, if price falls back to msrp and msrp falls back to invoice, price will not fall back to invoice.',
  properties: {
    [PriceRuleSource.FEED_PRICE]: {
      type: 'array',
      title: 'Feed Price Fallback',
      minItems: 0,
      maxItems: 2,
      items: {
        type: 'string',
        enum: [PriceRuleSource.MSRP, PriceRuleSource.INVOICE],
        enumNames: ['MSRP', 'Invoice'],
      },
    },
    [PriceRuleSource.MSRP]: {
      type: 'array',
      title: 'MSRP Fallback',
      minItems: 0,
      maxItems: 2,
      items: {
        type: 'string',
        enum: [PriceRuleSource.FEED_PRICE, PriceRuleSource.INVOICE],
        enumNames: ['Feed Price', 'Invoice'],
      },
    },
    [PriceRuleSource.INVOICE]: {
      type: 'array',
      title: 'Invoice Fallback',
      minItems: 0,
      maxItems: 2,
      items: {
        type: 'string',
        enum: [PriceRuleSource.FEED_PRICE, PriceRuleSource.MSRP],
        enumNames: ['Feed Price', 'MSRP'],
      },
    },
  },
};

export const getFeedSchema = (feedType, mappingTypes) => {
  if (feedType === 'csv') {
    return {
      title: 'Feed',
      type: 'object',
      required: ['feedType', 'name', 'fuelEfficiencyUnit'],
      properties: {
        feedType: { type: 'string', title: 'Feed Type', enum: feedTypes },
        name: { type: 'string', title: 'Name' },
        online: { type: 'boolean', title: 'Online', default: true },
        inStore: { type: 'boolean', title: 'In-Store', default: true },
        fuelEfficiencyUnit: {
          type: 'string',
          title: 'Fuel Efficiency Unit',
          enum: Object.values(FuelEfficiencyUnit),
          default: FuelEfficiencyUnit.MPG,
        },
        config: {
          type: 'object',
          required: ['fileName', 'type'],
          title: 'Configuration',
          properties: {
            fileName: { type: 'string', title: 'File Name' },
            type: { type: 'string', title: 'Type', enum: mappingTypes },
            inventoryId: { type: ['string', 'null'], title: 'Inventory ID' },
          },
        },
        priceFallbacks,
      },
    };
  }
  if (feedType === 'autodata') {
    return {
      title: 'Feed',
      type: 'object',
      required: ['feedType', 'name', 'fuelEfficiencyUnit'],
      properties: {
        feedType: { type: 'string', title: 'Feed Type', enum: feedTypes },
        name: { type: 'string', title: 'Name' },
        online: { type: 'boolean', title: 'Online', default: true },
        inStore: { type: 'boolean', title: 'In-Store', default: true },
        fuelEfficiencyUnit: {
          type: 'string',
          title: 'Fuel Efficiency Unit',
          enum: Object.values(FuelEfficiencyUnit),
          default: FuelEfficiencyUnit.MPG,
        },
        config: {
          type: 'object',
          title: 'Configuration',
        },
        priceFallbacks,
      },
    };
  }
  if (feedType === 'shiftFtp') {
    return {
      title: 'Feed',
      type: 'object',
      required: ['feedType', 'name', 'fuelEfficiencyUnit'],
      properties: {
        feedType: { type: 'string', title: 'Feed Type', enum: feedTypes },
        name: { type: 'string', title: 'Name' },
        online: { type: 'boolean', title: 'Online', default: true },
        inStore: { type: 'boolean', title: 'In-Store', default: true },
        fuelEfficiencyUnit: {
          type: 'string',
          title: 'Fuel Efficiency Unit',
          enum: Object.values(FuelEfficiencyUnit),
          default: FuelEfficiencyUnit.MPG,
        },
        config: {
          type: 'object',
          title: 'Configuration',
        },
        priceFallbacks,
      },
    };
  }
  return {
    title: 'Feed',
    type: 'object',
    required: ['feedType', 'name', 'fuelEfficiencyUnit'],
    properties: {
      feedType: { type: 'string', title: 'Feed Type', enum: feedTypes },
      name: { type: 'string', title: 'Name' },
      online: { type: 'boolean', title: 'Online', default: true },
      inStore: { type: 'boolean', title: 'In-Store', default: true },
      fuelEfficiencyUnit: {
        type: 'string',
        title: 'Fuel Efficiency Unit',
        enum: Object.values(FuelEfficiencyUnit),
        default: FuelEfficiencyUnit.MPG,
      },
      config: {
        type: 'object',
        required: ['url'],
        title: 'Configuration',
        properties: {
          url: { type: 'string', title: 'Api Url', format: 'uri' },
        },
      },
      priceFallbacks,
    },
  };
};

export const uiSchema = {
  config: {
    inventoryId: {
      'ui:help':
        'To reference a dealer within a common feed for multiple dealers',
    },
  },
  priceFallbacks: {
    classNames: 'col-md-5 config-group feed-price-fallbacks-config',
  },
};

export const validate = (formData, errors) => {
  const hasDuplicates = Object.values(formData.priceFallbacks || {}).find(
    fallback => uniq(fallback || []).length !== (fallback || []).length,
  );
  if (hasDuplicates) {
    errors.priceFallbacks.addError('Duplicate fallbacks are not allowed');
  }

  return errors;
};
